import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import lockr from 'lockr';
import axios from 'axios';
import { fetchApi, fetchApiAuth } from '../utils/api';
import { ResponseProps, Survey, GetSurveysPayload, Question } from './types';
import constants from '../constants';

const {
    LOCALSTORAGE_EXPIRES_KEY,
    AUTH_KEY,
} = constants;

export const updateSurvey = createAsyncThunk(
    'survey/update-survey',
    async (payload:any) => {
        const response:ResponseProps = await fetchApiAuth({
            method: 'POST',
            url: 'v1/survey/update-survey',
            body: payload,
        });
        return response.data;
    },
);

export const createSurvey = createAsyncThunk(
    'survey/create-survey',
    async (payload:Survey) => {
        const response:ResponseProps = await fetchApiAuth({
            method: 'POST',
            url: 'v1/survey/create-survey',
            body: payload,
        });
        return response.data;
    },
);

export const createSurveyQuestion = createAsyncThunk(
    'survey/create-survey-question',
    async (payload:Question) => {
        const response:ResponseProps = await fetchApiAuth({
            method: 'POST',
            url: 'v1/survey/create-survey-question',
            body: payload,
        });
        return response.data;
    },
);

export const updateSurveyQuestion = createAsyncThunk(
    'survey/update-survey-question',
    async (payload:Question) => {
        const response:ResponseProps = await fetchApiAuth({
            method: 'POST',
            url: 'v1/survey/update-survey-question',
            body: payload,
        });
        return response.data;
    },
);

export const getSurveys = createAsyncThunk(
    'survey/get-surveys',
    async (payload: GetSurveysPayload = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/survey/get-surveys',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getPremadeSurveys = createAsyncThunk(
    'survey/get-premade-surveys',
    async (payload: GetSurveysPayload = {}) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/survey/get-surveys',
                body: { ...payload, is_premade: 1 },
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSurveyBySlug = createAsyncThunk(
    'survey/get-survey-by-slug',
    async (payload:any) => {
        const response:ResponseProps = await fetchApi({
            method: 'POST',
            url: 'v1/survey/get-survey-by-slug',
            body: payload,
        });
        return response.data;
    },
);

export const submitSurveyResponse = createAsyncThunk(
    'survey/submit-survey-response',
    async (payload: any) => {
        try {
            const response: ResponseProps = await fetchApi({
                method: 'POST',
                url: 'v1/survey/submit-survey-response',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSurveySubmissions = createAsyncThunk(
    'survey/get-survey-submissions',
    async (payload: { surveyId: number }) => {
        try {
            const response: ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/survey/get-submissions',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSurveyStatistics = createAsyncThunk(
    'survey/get-survey-statistics',
    async (payload: { surveyId: number }) => {
        try {
            const response: ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/survey/get-statistics',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const downloadSurveyStatistics = createAsyncThunk(
    'survey/download-survey-statistics',
    async (payload: { surveyId: number }) => {
        try {
            const response = await fetchApiAuth({
                method: 'POST',
                url: 'v1/survey/download-statistics',
                body: payload,
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const duplicateSurvey = createAsyncThunk(
    'survey/duplicate-survey',
    async (payload: { surveyId: number }) => {
        try {
            const response: ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/survey/duplicate-survey',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const createSurveyFromPremade = createAsyncThunk(
    'survey/create-survey-from-premade',
    async (payload: { parent_survey_id: number }) => {
        try {
            const response: ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/survey/create-survey-from-premade',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const deleteSurvey = createAsyncThunk(
    'survey/delete-survey',
    async (payload: { surveyId: number }) => {
        try {
            await fetchApiAuth({
                method: 'POST',
                url: 'v1/survey/delete-survey',
                body: payload,
            });
            return payload;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const analyzeSurveyWithAI = createAsyncThunk(
    'survey/analyze-survey-with-ai',
    async (payload: { survey_id: number }) => {
        try {
            const response: ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/survey/analyze-with-ai',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Nepavyko atlikti AI analizės';
                notification.error({
                    message: 'AI analizės klaida',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);
